import React from 'react';
import { CustomerTemplate } from "@/components/CustomerTemplateManager";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export type OrderStatus = "미지정" | "부분 입금" | "입금 완료" | "출고 완료";
export const orderStatuses: OrderStatus[] = ["미지정", "부분 입금", "입금 완료", "출고 완료"];
// 노출 게이트(스펙 D10): 선택지 UI(드롭다운·배치 툴바)는 이 헬퍼를 쓸 것 —
// 계좌 연동(linked) 셀러만 '부분 입금'을 선택할 수 있다. 데이터 표시(pill 등)는 게이트 없음.
// eslint-disable-next-line react-refresh/only-export-components -- orderStatuses(기존 export)와 동일 사유: 상태 상수/헬퍼는 OrderControls와 한 파일에 co-locate(lint 베이스라인 유지, 기존 line 15와 동일 패턴)
export function selectableOrderStatuses(depositLinked: boolean): OrderStatus[] {
  return depositLinked ? orderStatuses : orderStatuses.filter((s) => s !== "부분 입금");
}

interface OrderControlsProps {
  activeCustomer: CustomerTemplate;
  isOrderKept: boolean;
  onKeepOrderChange: (checked: boolean | 'indeterminate') => void;
  currentOrderStatus: OrderStatus;
  onOrderStatusChange: (status: OrderStatus) => void;
  className?: string;
  'data-tour'?: string; // Added
  // 스펙 D10: 계좌 연동(linked) 셀러만 '부분 입금'을 선택할 수 있다(Home의 selectableOrderStatuses 결과 주입).
  statusOptions: OrderStatus[];
}

export const OrderControls: React.FC<OrderControlsProps> = ({
  activeCustomer,
  isOrderKept,
  onKeepOrderChange,
  currentOrderStatus,
  onOrderStatusChange,
  className,
  'data-tour': dataTour,
  statusOptions,
}) => {
  return (
    <div className={cn("flex flex-col sm:flex-row items-start sm:items-center gap-3", className)} data-tour={dataTour}>
      <div className="flex items-center gap-1.5">
        <Checkbox
          id={`keep-order-${activeCustomer.id}`}
          checked={isOrderKept}
          onCheckedChange={onKeepOrderChange}
          aria-label="주문 킵 여부"
        />
        <label
          htmlFor={`keep-order-${activeCustomer.id}`}
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer font-semibold text-yellow-800 bg-yellow-100 px-2 py-0.5 rounded-full whitespace-nowrap"
        >
          주문 보관(킵)
        </label>
      </div>
      <div className="flex items-center space-x-2 bg-blue-50 p-2 rounded-md">
        <span className="text-sm font-medium text-gray-700 whitespace-nowrap">주문상태:</span>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="text-xs h-8 w-[90px] flex items-center px-2">
              <span className="flex-1 text-left">{currentOrderStatus}</span> <ChevronDown className="w-3 h-3 flex-shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {statusOptions.map((status) => (
              <DropdownMenuItem
                key={status}
                onClick={() => onOrderStatusChange(status)}
                className="text-xs"
              >
                {status}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};